/**
 * LanguageSwitcher Component
 *
 * A CVA-based language selector UI component that allows users to switch
 * between supported languages. Displays language native names and integrates
 * with the useLanguageSwitch hook for state management.
 *
 * @module i18n/LanguageSwitcher
 * @see Requirements 2.1, 2.2, 2.5
 */

import React, { useCallback } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/cn';
import { useLanguageSwitch } from './useLanguageSwitch';
import { useTypedTranslation } from './useTypedTranslation';

/**
 * LanguageSwitcher variants using CVA (Class Variance Authority)
 * Defines all visual variations of the LanguageSwitcher component
 */
const languageSwitcherVariants = cva(
  // Base styles (always applied)
  [
    'appearance-none',
    'bg-white',
    'border border-gray-300',
    'rounded-md',
    'text-sm',
    'cursor-pointer',
    'transition-colors duration-200',
    'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
    'hover:border-gray-400',
  ],
  {
    variants: {
      variant: {
        default: ['px-3 py-2', 'pr-8'],
        compact: ['px-2 py-1', 'pr-6', 'text-xs'],
        minimal: ['px-2 py-1', 'border-transparent', 'bg-transparent', 'hover:bg-gray-100'],
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

/** Exported type for LanguageSwitcher variants */
export type LanguageSwitcherVariants = VariantProps<typeof languageSwitcherVariants>;

export interface LanguageSwitcherProps
  extends
    Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange'>,
    LanguageSwitcherVariants {
  /** Callback when language changes */
  onLanguageChange?: (languageCode: string) => void;
}

/**
 * Language selector component following Linkode design system.
 *
 * Features:
 * - Displays all supported languages with native names
 * - Integrates with useLanguageSwitch for state management
 * - Accessible with proper ARIA labels
 * - CVA-based variants for different visual styles
 *
 * @example
 * ```tsx
 * // Default usage
 * <LanguageSwitcher />
 *
 * // Compact variant for toolbars
 * <LanguageSwitcher variant="compact" />
 *
 * // Minimal variant for headers
 * <LanguageSwitcher variant="minimal" />
 *
 * // With change callback
 * <LanguageSwitcher onLanguageChange={(lang) => console.log('Changed to:', lang)} />
 * ```
 */
export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  variant,
  className,
  onLanguageChange,
  ...props
}) => {
  const { currentLanguage, supportedLanguages, switchLanguage } = useLanguageSwitch();
  const { t } = useTypedTranslation();

  const handleChange = useCallback(
    async (event: React.ChangeEvent<HTMLSelectElement>) => {
      const newLanguage = event.target.value;
      await switchLanguage(newLanguage);
      onLanguageChange?.(newLanguage);
    },
    [switchLanguage, onLanguageChange]
  );

  return (
    <select
      value={currentLanguage}
      onChange={handleChange}
      aria-label={t('common.selectLanguage')}
      className={cn(languageSwitcherVariants({ variant }), className)}
      {...props}
    >
      {supportedLanguages.map((lang) => (
        <option key={lang.code} value={lang.code}>
          {lang.nativeName}
        </option>
      ))}
    </select>
  );
};

// Export variants for external use (e.g., testing, Storybook)
export { languageSwitcherVariants };
